import { useState } from 'react';
import {
  View, Text, TextInput, ScrollView, TouchableOpacity, StyleSheet,
  Image, Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useExercises, useMuscleGroups } from '@/hooks/useExercises';
import { VideoPlayer } from '@/components/VideoPlayer';
import { Exercise } from '@/lib/types';

export function LibraryDesktop() {
  const [search, setSearch] = useState('');
  const [muscleGroup, setMuscleGroup] = useState<string | undefined>();
  const [selected, setSelected] = useState<Exercise | null>(null);
  const { exercises } = useExercises({ search, muscleGroup });
  const { groups: muscleGroups } = useMuscleGroups();

  const withVideo = exercises.filter(e => e.video_url).length;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>Exercícios</Text>
          <Text style={styles.subtitle}>
            {exercises.length} exercício{exercises.length === 1 ? '' : 's'} · {withVideo} com vídeo
          </Text>
        </View>
      </View>

      {/* Toolbar: busca + chips */}
      <View style={styles.searchBox}>
        <Ionicons name="search-outline" size={16} color="#888" />
        <TextInput
          style={styles.searchInput}
          placeholder="Buscar exercício…"
          placeholderTextColor="#aaa"
          value={search}
          onChangeText={setSearch}
        />
      </View>

      <View style={styles.filterChips}>
        <Chip label="Todos" active={!muscleGroup} onPress={() => setMuscleGroup(undefined)} />
        {muscleGroups.map(g => (
          <Chip
            key={g}
            label={g}
            active={muscleGroup === g}
            onPress={() => setMuscleGroup(g)}
          />
        ))}
      </View>

      {/* Grid */}
      {exercises.length === 0 ? (
        <View style={styles.empty}>
          <Ionicons name="barbell-outline" size={40} color="#ccc" />
          <Text style={styles.emptyText}>Nenhum exercício encontrado.</Text>
        </View>
      ) : (
        <View style={styles.grid}>
          {exercises.map(ex => (
            <Card key={ex.id} exercise={ex} onPress={setSelected} />
          ))}
        </View>
      )}

      {/* Modal de detalhe */}
      <Modal
        visible={!!selected}
        animationType="fade"
        transparent
        onRequestClose={() => setSelected(null)}
      >
        {selected && (
          <View style={styles.modalBackdrop}>
            <View style={styles.modalCard}>
              <View style={styles.modalHeader}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.modalName}>{selected.name}</Text>
                  <Text style={styles.modalMuscle}>{selected.muscle_group}</Text>
                </View>
                <TouchableOpacity onPress={() => setSelected(null)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                  <Ionicons name="close" size={22} color="#888" />
                </TouchableOpacity>
              </View>
              <View style={styles.modalVideo}>
                <VideoPlayer uri={selected.video_url} thumbnailUri={selected.thumbnail_url} autoPlay />
              </View>
              {selected.description ? (
                <Text style={styles.modalDesc}>{selected.description}</Text>
              ) : (
                <Text style={styles.modalDescMuted}>Sem descrição cadastrada.</Text>
              )}
            </View>
          </View>
        )}
      </Modal>
    </ScrollView>
  );
}

// ─── Sub-componentes ────────────────────────────────────────────────────────

function Chip({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <TouchableOpacity
      style={[styles.chip, active && styles.chipActive]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <Text style={[styles.chipText, active && styles.chipTextActive]}>{label}</Text>
    </TouchableOpacity>
  );
}

function Card({ exercise, onPress }: { exercise: Exercise; onPress: (e: Exercise) => void }) {
  const hasVideo = !!exercise.video_url;
  return (
    <TouchableOpacity
      style={styles.card}
      onPress={() => onPress(exercise)}
      activeOpacity={0.7}
    >
      <View style={styles.thumbWrap}>
        {exercise.thumbnail_url ? (
          <Image source={{ uri: exercise.thumbnail_url }} style={styles.thumb} resizeMode="cover" />
        ) : (
          <View style={styles.thumbPlaceholder}>
            <Ionicons name="barbell-outline" size={32} color="#ccc" />
          </View>
        )}
        {hasVideo && (
          <View style={styles.playBadge}>
            <Ionicons name="play" size={12} color="#fff" />
          </View>
        )}
      </View>
      <View style={styles.cardInfo}>
        <Text style={styles.cardName} numberOfLines={2}>{exercise.name}</Text>
        <Text style={styles.cardMuscle} numberOfLines={1}>{exercise.muscle_group}</Text>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  content: { padding: 24, paddingBottom: 48 },

  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  title: { fontSize: 22, fontWeight: '800', color: '#1A1A1A' },
  subtitle: { fontSize: 13, color: '#888', marginTop: 4 },

  searchBox: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: '#fff', borderWidth: 1, borderColor: '#EEE',
    paddingHorizontal: 14, paddingVertical: 10, borderRadius: 8,
    marginBottom: 12,
  },
  searchInput: { flex: 1, fontSize: 14, color: '#1A1A1A', outlineStyle: 'none' as never },

  filterChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 20 },
  chip: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 16, backgroundColor: '#fff', borderWidth: 1, borderColor: '#EEE' },
  chipActive: { backgroundColor: '#C8102E', borderColor: '#C8102E' },
  chipText: { fontSize: 12, color: '#555', fontWeight: '600' },
  chipTextActive: { color: '#fff' },

  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 14 },
  card: {
    width: 220,
    backgroundColor: '#fff',
    borderRadius: 10,
    borderWidth: 1, borderColor: '#EEE',
    overflow: 'hidden',
  },
  thumbWrap: { width: '100%', aspectRatio: 16 / 10, backgroundColor: '#F5F5F5', position: 'relative' },
  thumb: { width: '100%', height: '100%' },
  thumbPlaceholder: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  playBadge: {
    position: 'absolute', bottom: 8, right: 8,
    backgroundColor: '#C8102E', borderRadius: 12,
    width: 22, height: 22, alignItems: 'center', justifyContent: 'center',
  },
  cardInfo: { padding: 12 },
  cardName: { fontSize: 13, fontWeight: '700', color: '#1A1A1A' },
  cardMuscle: { fontSize: 11, color: '#888', marginTop: 4, textTransform: 'capitalize' },

  empty: { alignItems: 'center', justifyContent: 'center', paddingVertical: 60, gap: 10 },
  emptyText: { fontSize: 14, color: '#888' },

  modalBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center', justifyContent: 'center', padding: 24,
  },
  modalCard: {
    width: '100%', maxWidth: 640,
    backgroundColor: '#fff', borderRadius: 12, padding: 20,
  },
  modalHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginBottom: 14 },
  modalName: { fontSize: 18, fontWeight: '800', color: '#1A1A1A' },
  modalMuscle: { fontSize: 12, color: '#888', marginTop: 2, textTransform: 'capitalize' },
  modalVideo: { width: '100%', aspectRatio: 16 / 9, backgroundColor: '#000', borderRadius: 8, overflow: 'hidden', marginBottom: 14 },
  modalDesc: { fontSize: 14, color: '#444', lineHeight: 22 },
  modalDescMuted: { fontSize: 13, color: '#aaa', fontStyle: 'italic' },
});
